import React, { useState, useCallback, useContext } from "react";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";

import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import PopupState, { bindMenu } from "material-ui-popup-state";
import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  TableBody,
  TableCell,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  TableRow,
  Table,
  TableHead,
  Tooltip,
} from "@material-ui/core";
import {
  Edit,
  DeleteOutline,
  Facebook,
  Instagram,
  SettingsInputAntenna,
  WhatsApp,
} from "@material-ui/icons";

import FacebookLogin from "react-facebook-login/dist/facebook-login-render-props";

import TableRowSkeleton from "../../components/TableRowSkeleton";
import EmptyState from "../../components/EmptyState";
import { StatusBadge } from "../../components/ui";
import { usePanelStyles, ModalTitle, TableCard, RowActions } from "../../components/FormFields/panel";
import { ChannelTile, ConnectionStatus, channelLabel, connectionNumber } from "../../components/ConnectionIcon/ConnectionStatus";
import { getTokens } from "../../styles/tokens";
import { AuthContext } from "../../context/Auth/AuthContext";
import useCompanies from "../../hooks/useCompanies";
import api from "../../services/api";
import WhatsAppModalAdmin from "../../components/WhatsAppModalAdmin";
import ConfirmationModal from "../../components/ConfirmationModal";
import QrcodeModal from "../../components/QrcodeModal";
import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    menuIcon: { marginRight: 10 },
    nameCell: { display: "flex", alignItems: "center", gap: 12, minWidth: 180 },
    nameLine: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, fontWeight: 600, color: t.text },
    nameSub: { fontSize: 12.5, lineHeight: 1.4, color: t.textSecondary },
    sessionActions: { display: "flex", alignItems: "center", gap: 6 },
    // rótulos de botão vêm em minúsculas do i18n ("desconectar")
    cap: { display: "inline-block", "&::first-letter": { textTransform: "uppercase" } },
    nowrap: { whiteSpace: "nowrap" },
    deleteButton: { "&:hover": { color: `${t.mode === "dark" ? t.status.down.fg : t.semantic.error} !important` } },
  };
});

const WhatsAppModalCompany = ({
  open,
  onClose,
  whatsAppId,
  filteredWhatsapps,
  companyInfos,
}) => {
  //console.log(filteredWhatsapps,"teste")
  //console.log(companyInfos,"testeeeee")
  const classes = useStyles();
  const p = usePanelStyles();
  const { user, socket } = useContext(AuthContext);
  const { list } = useCompanies();
  const [loadingComp, setLoadingComp] = useState(false);
  const { whatsApps, loading } = useContext(WhatsAppsContext);
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const confirmationModalInitialState = {
    action: "",
    title: "",
    message: "",
    whatsAppId: "",
    open: false,
  };
  const [confirmModalInfo, setConfirmModalInfo] = useState(
    confirmationModalInitialState
  );

  const responseFacebook = (response) => {
    if (response.status !== "unknown") {
      const { accessToken, id } = response;

      api
        .post("/facebook", {
          facebookUserId: id,
          facebookUserToken: accessToken,
        })
        .then((response) => {
          toast.success(i18n.t("connections.facebook.success"));
        })
        .catch((error) => {
          toastError(error);
        });
    }
  };
  const responseInstagram = (response) => {
    if (response.status !== "unknown") {
      const { accessToken, id } = response;

      api
        .post("/facebook", {
          addInstagram: true,
          facebookUserId: id,
          facebookUserToken: accessToken,
        })
        .then((response) => {
          toast.success(i18n.t("connections.facebook.success"));
        })
        .catch((error) => {
          toastError(error);
        });
    }
  };

  const handleStartWhatsAppSession = async (whatsAppId) => {
    try {
      await api.post(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleRequestNewQrCode = async (whatsAppId) => {
    try {
      await api.put(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenWhatsAppModal = () => {
    setSelectedWhatsApp(null);
    setWhatsAppModalOpen(true);
  };

  const handleCloseWhatsAppModal = useCallback(() => {
    setWhatsAppModalOpen(false);
    setSelectedWhatsApp(null);
  }, [setSelectedWhatsApp, setWhatsAppModalOpen]);

  const handleOpenQrModal = (whatsApp) => {
    setSelectedWhatsApp(whatsApp);
    setQrModalOpen(true);
  };

  const handleCloseQrModal = useCallback(() => {
    setSelectedWhatsApp(null);
    setQrModalOpen(false);
  }, [setQrModalOpen, setSelectedWhatsApp]);

  const handleEditWhatsApp = (whatsApp) => {
    setSelectedWhatsApp(whatsApp);
    setWhatsAppModalOpen(true);
  };

  const handleOpenConfirmationModal = (action, whatsAppId) => {
    if (action === "disconnect") {
      setConfirmModalInfo({
        action: action,
        title: i18n.t("connections.confirmationModal.disconnectTitle"),
        message: i18n.t("connections.confirmationModal.disconnectMessage"),
        whatsAppId: whatsAppId,
      });
    }

    if (action === "delete") {
      setConfirmModalInfo({
        action: action,
        title: i18n.t("connections.confirmationModal.deleteTitle"),
        message: i18n.t("connections.confirmationModal.deleteMessage"),
        whatsAppId: whatsAppId,
      });
    }
    setConfirmModalOpen(true);
  };

  const handleSubmitConfirmationModal = async () => {
    if (confirmModalInfo.action === "disconnect") {
      try {
        await api.delete(`/whatsappsession/${confirmModalInfo.whatsAppId}`);
      } catch (err) {
        toastError(err);
      }
    }

    if (confirmModalInfo.action === "delete") {
      try {
        await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`);
        toast.success(i18n.t("connections.toasts.deleted"));
      } catch (err) {
        toastError(err);
      }
    }

    setConfirmModalInfo(confirmationModalInitialState);
  };

  const renderStatusToolTips = (whatsApp) => {
    return <ConnectionStatus status={whatsApp.status} />;
  };

  const renderActionButtons = (whatsApp) => {
    return (
      <>
        {whatsApp.channel !== "webchat" && whatsApp.status === "qrcode" && (
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={() => handleOpenQrModal(whatsApp)}
          >
            {i18n.t("connections.buttons.qrcode")}
          </Button>
        )}
        {whatsApp.channel !== "webchat" && whatsApp.status === "DISCONNECTED" && (
          <>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={() => handleStartWhatsAppSession(whatsApp.id)}
            >
              {i18n.t("connections.buttons.tryAgain")}
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => handleRequestNewQrCode(whatsApp.id)}
            >
              {i18n.t("connections.buttons.newQr")}
            </Button>
          </>
        )}
        {(whatsApp.status === "CONNECTED" ||
          whatsApp.status === "PAIRING" ||
          whatsApp.status === "TIMEOUT") && (
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              handleOpenConfirmationModal("disconnect", whatsApp.id);
            }}
          >
            <span className={classes.cap}>{i18n.t("connections.buttons.disconnect")}</span>
          </Button>
        )}
        {whatsApp.status === "OPENING" && (
          <Button size="small" variant="outlined" disabled color="default">
            {i18n.t("connections.buttons.connecting")}
          </Button>
        )}
      </>
    );
  };

  const handleClose = () => {
    onClose();
  };

  const isAdmin = user.profile === "admin";
  const columns = isAdmin ? 5 : 3;
  const total = filteredWhatsapps?.length || 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      classes={{ paper: p.dialogPaper }}
    >
      <ModalTitle
        title={`${i18n.t("connections.connections")} ${companyInfos?.name || ""}`}
        subtitle={total === 1 ? "1 conexão cadastrada" : `${total} conexões cadastradas`}
        onClose={handleClose}
      />
      <ConfirmationModal
        title={confirmModalInfo.title}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={handleSubmitConfirmationModal}
      >
        {confirmModalInfo.message}
      </ConfirmationModal>
      <QrcodeModal
        open={qrModalOpen}
        onClose={handleCloseQrModal}
        whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id}
      />
      <WhatsAppModalAdmin
        open={whatsAppModalOpen}
        onClose={handleCloseWhatsAppModal}
        whatsAppId={!qrModalOpen && selectedWhatsApp?.id}
      />
      {/* Menu de criação herdado: nunca teve botão que o abrisse neste modal; mantido como estava. */}
      <PopupState variant="popover" popupId="demo-popup-menu">
        {(popupState) => (
          <React.Fragment>
            <Menu {...bindMenu(popupState)}>
              <MenuItem
                onClick={() => {
                  handleOpenWhatsAppModal();
                  popupState.close();
                }}
              >
                <WhatsApp fontSize="small" className={classes.menuIcon} />
                WhatsApp
              </MenuItem>
              <FacebookLogin
                appId={process.env.REACT_APP_FACEBOOK_APP_ID}
                autoLoad={false}
                fields="name,email,picture"
                version="13.0"
                scope="public_profile,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagementnt,business_management"
                callback={responseFacebook}
                render={(renderProps) => (
                  <MenuItem onClick={renderProps.onClick}>
                    <Facebook fontSize="small" className={classes.menuIcon} />
                    Facebook
                  </MenuItem>
                )}
              />

              <FacebookLogin
                appId={process.env.REACT_APP_FACEBOOK_APP_ID}
                autoLoad={false}
                fields="name,email,picture"
                version="13.0"
                scope="public_profile,instagram_basic,instagram_manage_messages,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement,business_management"
                callback={responseInstagram}
                render={(renderProps) => (
                  <MenuItem onClick={renderProps.onClick}>
                    <Instagram fontSize="small" className={classes.menuIcon} />
                    Instagram
                  </MenuItem>
                )}
              />
            </Menu>
          </React.Fragment>
        )}
      </PopupState>
      <DialogContent className={p.modalBody}>
        <TableCard>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Canal</TableCell>
                <TableCell>{i18n.t("connections.table.status")}</TableCell>
                {isAdmin && (
                  <TableCell>{i18n.t("connections.table.session")}</TableCell>
                )}
                <TableCell>{i18n.t("connections.table.lastUpdate")}</TableCell>
                {isAdmin && (
                  <TableCell align="right">{i18n.t("connections.table.actions")}</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRowSkeleton columns={columns} />
              ) : (
                <>
                  {filteredWhatsapps?.length > 0 &&
                    filteredWhatsapps.map((whatsApp) => (
                      <TableRow key={whatsApp.id}>
                        <TableCell>
                          <div className={classes.nameCell}>
                            <ChannelTile channel={whatsApp.channel} color={whatsApp.color} small />
                            <div style={{ minWidth: 0 }}>
                              <div className={classes.nameLine}>
                                {whatsApp?.name}
                                {whatsApp.isDefault && (
                                  <StatusBadge color="brand" dot={false} size="sm" label={i18n.t("connections.table.default")} />
                                )}
                              </div>
                              <div className={classes.nameSub}>
                                {[channelLabel(whatsApp.channel), connectionNumber(whatsApp)].filter(Boolean).join(" · ")}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{renderStatusToolTips(whatsApp)}</TableCell>
                        {isAdmin && (
                          <TableCell>
                            <div className={classes.sessionActions}>{renderActionButtons(whatsApp)}</div>
                          </TableCell>
                        )}
                        <TableCell className={`${classes.nowrap} ${p.cellMuted}`}>
                          {whatsApp.updatedAt ? format(parseISO(whatsApp.updatedAt), "dd/MM/yy HH:mm") : "—"}
                        </TableCell>
                        {isAdmin && (
                          <TableCell align="right">
                            <RowActions>
                              <Tooltip title="Editar">
                                <IconButton
                                  size="small"
                                  aria-label={`Editar ${whatsApp?.name}`}
                                  onClick={() => handleEditWhatsApp(whatsApp)}
                                >
                                  <Edit />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Excluir">
                                <IconButton
                                  size="small"
                                  className={classes.deleteButton}
                                  aria-label={`Excluir ${whatsApp?.name}`}
                                  onClick={(e) => {
                                    handleOpenConfirmationModal(
                                      "delete",
                                      whatsApp.id
                                    );
                                  }}
                                >
                                  <DeleteOutline />
                                </IconButton>
                              </Tooltip>
                            </RowActions>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  {!total && (
                    <TableRow>
                      <TableCell colSpan={columns}>
                        <EmptyState
                          icon={<SettingsInputAntenna />}
                          title="Nenhuma conexão cadastrada"
                          message="Esta empresa ainda não tem canais de atendimento."
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </TableCard>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(WhatsAppModalCompany);
